/**
 * Remembers which items this client just removed from a list, so a server read cannot put one back.
 *
 * List reads are eventually consistent, so they can still return an item the server has already
 * deleted; and a read that was already in flight when the delete committed resolves afterwards
 * regardless of consistency mode. Either way a catch-up merge would re-add the item, and the user
 * watches something they just deleted reappear.
 *
 * The memo is a WINDOW rather than "suppress until the server stops returning it". That latter shape
 * works for a one-way per-user flag (see InsightsModel.pendingMutations), where a server that still
 * disagrees is simply behind. An item's absence is not one-way: a co-owner can share a note back, or
 * make a private one public, so suppressing until the server agreed would hide a live item for good.
 *
 * Only worth consulting on a POLLING path, which runs again and so merely defers the item. Do not
 * consult it on a one-shot event (a websocket frame): there is no retry, so suppressing there drops
 * the update entirely and a genuine restore stays invisible until some later catch-up.
 */
export class RecentLocalDeletions {
    constructor(private windowMs: number = 30000) {}

    // performance.now() rather than Date.now() because it is monotonic. A device clock correction
    // (an NTP jump after a long sleep) lands on foreground, which is exactly when the catch-up runs,
    // and a backwards jump against a wall clock would hold the window open far longer than intended.
    private deletedAt = new Map<string, number>();

    record = (id: string) => {
        // an id whose window has passed will never be asked about again once the server stops
        // returning it, so sweep here rather than letting the map grow for the whole session
        this.forgetExpired();
        this.deletedAt.set(id, performance.now());
    };

    isRecent = (id: string): boolean => {
        const at = this.deletedAt.get(id);
        if (at === undefined) {
            return false;
        }
        if (performance.now() - at < this.windowMs) {
            return true;
        }
        this.deletedAt.delete(id);
        return false;
    };

    private forgetExpired = () => {
        const now = performance.now();
        for (const [id, at] of this.deletedAt) {
            if (now - at >= this.windowMs) {
                this.deletedAt.delete(id);
            }
        }
    };
}
